import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

// Dropdown dengan kotak pencarian: ketik untuk memfilter, hasil tersorot.
export function SearchableSelect({ options, value, onChange, placeholder = "— Pilih —", testId = "searchable-select", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { setHi(0); }, [query]);
  useEffect(() => {
    listRef.current?.children[hi]?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const choose = (o) => { onChange(o.value); setOpen(false); setQuery(""); };

  const highlight = (label) => {
    const q = query.trim();
    if (!q) return label;
    const i = label.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return label;
    return (
      <>
        {label.slice(0, i)}
        <mark className="bg-yellow-200 rounded-sm px-0">{label.slice(i, i + q.length)}</mark>
        {label.slice(i + q.length)}
      </>
    );
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input
          type="text"
          className="pp-input pr-8"
          placeholder={placeholder}
          value={open ? query : (selected?.label || "")}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          disabled={disabled}
          data-testid={testId}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); if (open && filtered[hi]) choose(filtered[hi]); }
            else if (e.key === "Escape") setOpen(false);
          }}
        />
        <ChevronDown className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
      {open && (
        <div ref={listRef} className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testId}-dropdown`}>
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400 italic">Tidak ditemukan</div>}
          {filtered.map((o, i) => (
            <button
              type="button"
              key={o.value}
              className={`w-full text-left px-3 py-2 text-sm border-b border-slate-50 last:border-0 ${i === hi ? "bg-indigo-50" : ""} ${o.value === value ? "font-semibold text-indigo-700" : "text-slate-700"}`}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setHi(i)}
              data-testid={`${testId}-opt-${o.value}`}
            >
              {highlight(o.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
